/**
 * Fonctions de formatage pour remplacer les champs formatted* du cache
 * Économise ~1MB de mémoire en générant ces chaînes à la demande
 * Phase 9.1: Ajouter calculs pour birthYear, deathYear, age, deceased
 */

import { generateEventDescription } from '../../utils/eventDescriptionGenerator.js';
import { enrichIndividualTownsWithCoordinates } from '../../utils/coordinatesLookup.js';
import familyTownsStore from '../../tabs/familyMap/stores/familyTownsStore.js';
import { dateProcessor } from '../processors/dateProcessor.js';
import gedcomDataStore from '../stores/gedcomDataStore.js';

// Phase 6: Middleware de décompression transparent pour événements
import { decompressEventArray, decompressEvent } from '@fbracq/read-gedcom-geneafan/src/compression/eventCompression.js';

/**
 * Version corrigée de decompressEvent qui préserve les métadonnées complètes
 * + Phase 6.5: Décompression profession/position
 */
export function decompressEventWithMetadata(compressedEvent) {
    if (!compressedEvent || !compressedEvent.t) return compressedEvent;

    const event = decompressEvent(compressedEvent);

    // CORRECTION: Préserver l'objet m complet pour nos fonctions
    if (compressedEvent.m) {
        event.m = compressedEvent.m;

        // 🆕 Phase 6.5: Décompression manuelle profession/position
        // (override car node_modules peut ne pas être à jour)
        if (compressedEvent.m.pf) {
            event.profession = compressedEvent.m.pf;
        }
        if (compressedEvent.m.ps) {
            event.position = compressedEvent.m.ps;
        }
        if (compressedEvent.m.at) {
            event.activity_type = compressedEvent.m.at;
        }
    }

    // Décompression date_end (d2) → date_end pour affichage période
    if (compressedEvent.d2) {
        const d2 = compressedEvent.d2.toString();
        if (d2.endsWith('0101') && d2.length === 8) {
            event.date_end = d2.substring(0, 4);
        } else if (d2.endsWith('01') && d2.length === 8) {
            event.date_end = `${d2.substring(4, 6)}/${d2.substring(0, 4)}`;
        } else if (d2.length === 8) {
            event.date_end = `${d2.substring(6, 8)}/${d2.substring(4, 6)}/${d2.substring(0, 4)}`;
        } else {
            event.date_end = d2;
        }
    }

    return event;
}

/**
 * Version corrigée de decompressEventArray qui préserve les métadonnées
 */
function decompressEventArrayWithMetadata(compressedEvents) {
    if (!Array.isArray(compressedEvents)) return compressedEvents;
    return compressedEvents.map(decompressEventWithMetadata);
}
// Phase 7: Plus besoin de décompression des champs - format direct compressé v4.4

/**
 * Middleware transparent - récupère les événements décompressés
 * Utilise le service hybride pour les types inconnus
 * @param {Object} individual - L'individu
 * @returns {Promise<Array>} - Événements décompressés avec types enrichis
 */
export async function getDecompressedEventsAsync(individual) {
    if (!individual || !individual.e) return [];
    
    const decompressed = decompressEventArrayWithMetadata(individual.e);
    
    // Enrichir les types inconnus via le service
    const enrichedPromises = decompressed.map(async (event) => {
        // Si le type n'est pas reconnu (reste un code court)
        if (event.type && event.type.length <= 3 && !isKnownEventType(event.type)) {
            const { default: dictionaryService } = await import('../services/gedcomDictionaryService.js');
            const enriched = await dictionaryService.decompress(event.type);
            return {
                ...event,
                type: enriched.type,
                typeDescription: enriched.description
            };
        }
        return event;
    });
    
    return Promise.all(enrichedPromises);
}

/**
 * Version synchrone pour compatibilité (utilise uniquement le dictionnaire statique)
 * Tous les événements (GEDCOM + AI) sont unifiés dans individual.e
 * Les événements AI sont identifiés par m.src === 'ai'
 * @param {Object} individual - L'individu
 * @returns {Array} - Événements décompressés
 */
export function getDecompressedEvents(individual) {
    if (!individual) return [];

    if (individual.e && Array.isArray(individual.e)) {
        return decompressEventArrayWithMetadata(individual.e);
    }

    return [];
}

/**
 * Vérifie si un type d'événement est connu
 */
function isKnownEventType(type) {
    const knownTypes = [
        'birth', 'death', 'marriage', 'divorce', 'occupation',
        'graduation', 'military-service', 'residence', 'baptism',
        'child-birth', 'adoption', 'education', 'retirement'
    ];
    return knownTypes.includes(type);
}

/**
 * Extrait la date de naissance depuis les événements
 * @param {Object} individual - L'individu
 * @returns {string|null} - Date de naissance ou null
 */
export function getBirthDate(individual) {
    if (!individual) return null;

    // Phase 8.2: Extraction depuis individualEvents
    const events = getDecompressedEvents(individual);
    const birthEvent = events.find(e => e.type === 'birth' || e.t === 'fb' || e.type === 'fb');
    return birthEvent?.date || individual.birthDate || null;
}

/**
 * Extrait la ville de naissance (townKey) depuis les événements
 * @param {Object} individual - L'individu
 * @returns {string|null} - Clé de ville de naissance ou null
 */
export function getBirthTownKey(individual) {
    if (!individual) return null;

    // Phase 8.2: Extraction depuis individualEvents
    const events = getDecompressedEvents(individual);
    const birthEvent = events.find(e => e.type === 'birth' || e.t === 'fb' || e.type === 'fb');
    return birthEvent?.townKey || birthEvent?.l || individual.birthTownKey || null;
}

/**
 * Calcule l'année de naissance à partir de birthDate
 * @param {Object} individual - L'individu
 * @returns {string} - Année de naissance ou chaîne vide
 */
export function getBirthYear(individual) {
    const birthDate = getBirthDate(individual);
    if (!birthDate) return '';
    return dateProcessor.extractYear(birthDate);
}

/**
 * Extrait la date de décès depuis les événements
 * @param {Object} individual - L'individu
 * @returns {string|null} - Date de décès ou null
 */
export function getDeathDate(individual) {
    if (!individual) return null;

    // Phase 8.2: Extraction depuis individualEvents
    const events = getDecompressedEvents(individual);
    const deathEvent = events.find(e => e.type === 'death' || e.t === 'fd' || e.type === 'fd');
    return deathEvent?.date || individual.deathDate || null;
}

/**
 * Extrait la ville de décès (townKey) depuis les événements
 * @param {Object} individual - L'individu
 * @returns {string|null} - Clé de ville de décès ou null
 */
export function getDeathTownKey(individual) {
    if (!individual) return null;

    // Phase 8.2: Extraction depuis individualEvents
    const events = getDecompressedEvents(individual);
    const deathEvent = events.find(e => e.type === 'death' || e.t === 'fd' || e.type === 'fd');
    return deathEvent?.townKey || deathEvent?.l || individual.deathTownKey || null;
}

/**
 * Calcule l'année de décès à partir de deathDate
 * @param {Object} individual - L'individu
 * @returns {string} - Année de décès ou chaîne vide
 */
export function getDeathYear(individual) {
    const deathDate = getDeathDate(individual);
    if (!deathDate) return '';
    return dateProcessor.extractYear(deathDate);
}

/**
 * Calcule l'âge d'un individu
 * @param {Object} individual - L'individu
 * @returns {number|null} - Âge ou null
 */
export function getAge(individual) {
    const birthDate = getBirthDate(individual);
    if (!birthDate) return null;
    
    const deathDate = getDeathDate(individual);
    if (deathDate) {
        // Âge au décès
        return dateProcessor.calculateAge(birthDate, deathDate);
    } else {
        // Âge actuel (si vivant)
        if (isDeceased(individual)) return null;
        return dateProcessor.calculateAge(birthDate);
    }
}

/**
 * Détermine si un individu est décédé
 * @param {Object} individual - L'individu
 * @returns {boolean} - True si décédé
 */
export function isDeceased(individual) {
    if (!individual) return true;
    
    // Si on a une date de décès, la personne est décédée
    if (getDeathDate(individual)) return true;
    
    // Si pas de date de naissance, on considère décédé par défaut
    const birthDate = getBirthDate(individual);
    if (!birthDate) return true;
    
    // Si né il y a plus de 105 ans, probablement décédé
    const birthYear = getBirthYear(individual);
    const currentYear = new Date().getFullYear();
    return birthYear && (currentYear - parseInt(birthYear)) > 105;
}

/**
 * Formate la naissance d'un individu
 * @param {Object} individual - L'individu
 * @returns {string} - Description formatée de la naissance
 */
export function formatBirth(individual) {
    if (!individual) return '';
    
    const date = getBirthDate(individual) || '';
    // Phase 8.2: Récupérer le lieu depuis les événements
    let place = '';
    const events = getDecompressedEvents(individual);
    const birthEvent = events.find(e => e.type === 'birth' || e.t === 'fb' || e.type === 'fb');
    if (birthEvent?.townKey || birthEvent?.l) {
        const townKey = birthEvent.townKey || birthEvent.l;
        const townData = familyTownsStore.getTownsData(townKey);
        place = townData?.town || townKey;
    } else if (individual.birthTownKey) {
        // Fallback pour compatibilité
        const townData = familyTownsStore.getTownsData(individual.birthTownKey);
        place = townData?.town || individual.birthTownKey;
    }
    
    if (!date && !place) return '';
    
    if (place && place !== 'lieu inconnu') {
        return date ? `${date} à ${place}` : `à ${place}`;
    }
    
    return date;
}

/**
 * Formate la mort d'un individu
 * @param {Object} individual - L'individu
 * @returns {string} - Description formatée de la mort
 */
export function formatDeath(individual) {
    if (!individual) return '';
    
    // Phase 9.1: Utiliser les fonctions calculées
    const deceased = isDeceased(individual);
    const age = getAge(individual);
    
    // Si pas décédé
    if (!deceased) {
        if (age) {
            return `${age} ans`;
        }
        return '';
    }
    
    const date = getDeathDate(individual) || '';
    // Phase 8.2: Récupérer le lieu depuis les événements
    let place = '';
    const events = getDecompressedEvents(individual);
    const deathEvent = events.find(e => e.type === 'death' || e.t === 'fd' || e.type === 'fd');
    if (deathEvent?.townKey || deathEvent?.l) {
        const townKey = deathEvent.townKey || deathEvent.l;
        const townData = familyTownsStore.getTownsData(townKey);
        place = townData?.town || townKey;
    } else if (individual.deathTownKey) {
        // Fallback pour compatibilité
        const townData = familyTownsStore.getTownsData(individual.deathTownKey);
        place = townData?.town || individual.deathTownKey;
    }
    
    let result = date;
    
    if (place && place !== 'lieu inconnu') {
        result = date ? `${date} à ${place}` : `à ${place}`;
    }
    
    if (age && date) {
        result += ` (${age} ans)`;
    }
    
    return result;
}

/**
 * Formate les événements d'un individu
 * @param {Object} individual - L'individu
 * @returns {string[]} - Tableau de descriptions d'événements
 */
export function formatEvents(individual) {
    if (!individual) return [];
    
    // Phase 6: Utiliser la décompression transparente
    const expandedEvents = getDecompressedEvents(individual);
    
    return expandedEvents
        .map(event => generateEventDescription(event, individual))
        .filter(Boolean);
}

/**
 * Formate les occupations d'un individu
 * @param {Object} individual - L'individu
 * @returns {string} - Occupations formatées
 */
export function formatOccupations(individual) {
    if (!individual) return '';

    // Phase 6: Décompression transparente
    const events = getDecompressedEvents(individual);

    const occupations = events
        .filter(event => event.type === 'occupation')
        .map(event => {
            // Phase 6.5: Prefer profession, then position, then legacy
            const parts = [];
            if (event.profession) parts.push(event.profession);
            if (event.position) parts.push(event.position);
            if (parts.length === 0 && event.occupation) parts.push(event.occupation);
            return parts.join(', ');
        })
        .filter(Boolean);
    
    return occupations.join(', ');
}

/**
 * Formate les mariages d'un individu
 * @param {Object} individual - L'individu
 * @param {Map} individualsCache - Cache des individus
 * @returns {string} - Mariages formatés
 */
export function formatMarriages(individual, individualsCache) {
    if (!individual || !individualsCache) return '';
    
    const events = getDecompressedEvents(individual);
    
    const marriages = events
        .filter(event => event.type === 'marriage' && event.spouseId)
        .map(event => {
            const spouse = individualsCache.get(event.spouseId);
            if (!spouse) return null;
            
            let text = `${getName(spouse) || ''} ${getSurname(spouse) || ''}`.trim();
            if (event.date) text += ` (${event.date})`;
            
            return text;
        })
        .filter(Boolean);
    
    return marriages.join(', ');
}

/**
 * Formate les frères et sœurs d'un individu
 * @param {Object} individual - L'individu
 * @param {Map} individualsCache - Cache des individus
 * @returns {string} - Frères et sœurs formatés
 */
export function formatSiblings(individual, individualsCache) {
    if (!individual || !individualsCache || !individual.b) return '';
    
    const siblings = individual.b
        .map(siblingId => {
            const sibling = individualsCache.get(siblingId);
            if (!sibling) return null;
            
            return `${getName(sibling) || ''} ${getSurname(sibling) || ''}`.trim();
        })
        .filter(Boolean);
    
    return siblings.join(', ');
}

/**
 * Formate le lien d'une personne
 * @param {Object} individual - L'individu
 * @returns {string} - Lien HTML formaté
 */
export function formatPersonLink(individual, individualId = null) {
    if (!individual) return '';
    
    const name = `${getName(individual) || ''} ${getSurname(individual) || ''}`.trim();
    // Phase 11: ID doit être passé en paramètre maintenant
    const id = individualId || individual.i || 'unknown';
    return `<a href="#" class="person-link" data-person-id="${id}">${name}</a>`;
}

/**
 * Génère toutes les propriétés formatées d'un coup
 * @param {Object} individual - L'individu
 * @param {Map} individualsCache - Cache des individus
 * @returns {Object} - Objet avec toutes les propriétés formatées
 */
export function generateFormattedProperties(individual, individualsCache) {
    return {
        formattedBirth: formatBirth(individual),
        formattedDeath: formatDeath(individual),
        formattedOccupations: formatOccupations(individual),
        formattedMarriages: formatMarriages(individual, individualsCache),
        formattedSiblings: formatSiblings(individual, individualsCache),
        personLink: formatPersonLink(individual)
    };
}

/**
 * Wrapper pour maintenir la compatibilité avec le code existant
 * Ajoute les propriétés formatées à la volée
 */
export function wrapIndividualWithFormatters(individual, individualsCache) {
    if (!individual) return null;
    
    // Phase 7: Plus besoin de décompression - individual a déjà les champs optimaux (fa, pf, fn, etc.)
    // Format direct compressé v4.4 - champs créés natifs
    
    // Créer un proxy qui génère les formatted* à la demande
    return new Proxy(individual, {
        get(target, prop) {
            switch(prop) {
                // Phase 8.2: Compatibilité - extraire depuis events si nécessaire
                case 'birthDate':
                    return target.birthDate || getBirthDate(target);
                case 'deathDate':
                    return target.deathDate || getDeathDate(target);
                case 'birthTownKey':
                    return target.birthTownKey || getBirthTownKey(target);
                case 'deathTownKey':
                    return target.deathTownKey || getDeathTownKey(target);
                case 'birthYear':
                    return getBirthYear(target);
                case 'deathYear':
                    return getDeathYear(target);
                case 'age':
                    return getAge(target);
                case 'deceased':
                    return isDeceased(target);
                // Champs formatés
                case 'formattedBirth':
                    return formatBirth(target);
                case 'formattedDeath':
                    return formatDeath(target);
                case 'formattedOccupations':
                    return formatOccupations(target);
                case 'formattedMarriages':
                    return formatMarriages(target, individualsCache);
                case 'formattedSiblings':
                    return formatSiblings(target, individualsCache);
                case 'personLink':
                    return formatPersonLink(target);
                case 'individualTowns':
                    // Enrichir les villes avec les coordonnées à la demande
                    return enrichIndividualTownsWithCoordinates(target.t);
                case 'name':
                    // Phase 12: Extraction depuis fn si nécessaire
                    return target.name !== undefined ? target.name : getName(target);
                case 'surname':
                    // Phase 12: Extraction depuis fn si nécessaire
                    return target.surname !== undefined ? target.surname : getSurname(target);
                default:
                    return target[prop];
            }
        }
    });
}

/**
 * Phase 12: Extraction name depuis le champ compressé fn
 * @param {Object} individual - L'individu avec potentiellement un champ fn
 * @returns {string|null} - Le prénom extrait
 */
export function getName(individual) {
    if (!individual) return null;
    
    // Si le champ name existe encore, l'utiliser
    if (individual.name !== undefined) return individual.name;
    
    // Sinon extraire depuis fn
    if (individual.fn) {
        const [surname, name] = individual.fn.split('|');
        return name || null;
    }
    
    return null;
}

/**
 * Extrait le premier prénom d'un individu.
 * "Jean Pierre Marie" → "Jean"
 * @param {Object} individual
 * @returns {string|null}
 */
export function getFirstName(individual) {
    const fullName = getName(individual);
    if (!fullName) return null;
    return fullName.split(/\s+/)[0] || null;
}

/**
 * Phase 12: Extraction surname depuis le champ compressé fn
 * @param {Object} individual - L'individu avec potentiellement un champ fn
 * @returns {string|null} - Le nom de famille extrait
 */
export function getSurname(individual) {
    if (!individual) return null;
    
    // Si le champ surname existe encore, l'utiliser
    if (individual.surname !== undefined) return individual.surname;
    
    // Sinon extraire depuis fn
    if (individual.fn) {
        const [surname] = individual.fn.split('|');
        return surname || null;
    }
    
    return null;
}

// Phase 14: Dictionnaire de décompression gender
const GENDER_DECOMPRESSION = {
    'M': 'male',
    'F': 'female',
    'U': 'unknown'
};

/**
 * Phase 14: Extraction gender avec décompression si nécessaire
 * @param {Object} individual - L'individu
 * @returns {string|null} - Le gender décompressé
 */
export function getGender(individual) {
    if (!individual) return null;
    
    // Phase 10: Priorité au champ compressé 'g', fallback sur 'gender'
    const genderValue = individual.g !== undefined ? individual.g : individual.gender;
    if (!genderValue) return null;
    
    // Décompresser si c'est un format compressé (M, F, U)
    return GENDER_DECOMPRESSION[genderValue] || genderValue;
}

/**
 * Récupère les données de mariage d'un individu depuis familiesCache
 * @param {Object} individual - L'individu
 * @returns {Object|null} - Données de mariage décompressées ou null
 */
export function getMarriageData(individual) {
    if (!individual || !individual.fa) return null;
    
    const familyIds = individual.fa || [];
    
    for (const familyId of familyIds) {
        const family = gedcomDataStore.familiesCache.get(familyId);
        if (family && family.e) {
            // Chercher événements de mariage dans les événements familiaux
            const marriageEvent = family.e.find(e => 
                e.t === 'fm' || e.t === 'marriage-contract' || e.t.includes('marriage')
            );
            
            if (marriageEvent) {
                // Décompresser l'événement pour compatibilité
                return decompressEvent(marriageEvent);
            }
        }
    }
    
    return null;
}

/**
 * Récupère la couleur de fond pour la coloration par département
 * @param {Object} individual - L'individu
 * @returns {string} - Couleur de fond ou blanc par défaut
 */
export function getBgColor(individual) {
    if (!individual) return '#FFFFFF';
    
    // Priorité au champ bgColor s'il existe
    if (individual.bgColor) {
        return individual.bgColor;
    }
    
    // TODO: Implémenter la logique de couleur par département
    // basée sur birthTownKey ou autre critère géographique
    
    return '#FFFFFF';
}